import React, { useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import LoginScreen from './screens/LoginScreen';
import HomeScreen from './screens/HomeScreen';
import UserSettingsScreen from './screens/UserSettingsScreen';
import ChallengeScreen from './screens/ChallengeScreen';
import { ChallengeProgressScreen } from './screens/ChallengeProgressScreen';
import LeaderboardScreen from './screens/LeaderboardScreen';
import SettingsScreen from './screens/SettingsScreen';
import JoinChallengeScreen from './screens/JoinChallengeScreen';
import { AcceptFamilyInvitation } from './pages/AcceptFamilyInvitation';
import LandingPage from './pages/LandingPage';
import ContactSalesPage from './pages/ContactSalesPage';
import TermsOfUsePage from './pages/TermsOfUsePage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import AboutUsPage from './pages/AboutUsPage';
import OnboardingPage from './pages/OnboardingPage';
import SubscriptionPage from './pages/SubscriptionPage';
import SubscriptionUpgradePage from './pages/SubscriptionUpgradePage';
import SubscriptionSuccessPage from './pages/SubscriptionSuccessPage';
import SubscriptionCancelPage from './pages/SubscriptionCancelPage';
import AdminSubscriptionLoginPage from './pages/AdminSubscriptionLoginPage';
import ChallengesPage from './pages/subscription/ChallengesPage';
import ReportingPage from './pages/subscription/ReportingPage';
import OrganizationsPage from './pages/subscription/OrganizationsPage';
import UsersPage from './pages/subscription/UsersPage';
import LeaderboardsPage from './pages/subscription/LeaderboardsPage';
import SettingsPage from './pages/subscription/SettingsPage';
import InvoicesPage from './pages/subscription/InvoicesPage';
import HistoryPage from './pages/subscription/HistoryPage';
import AzureADCallback from './components/AzureADCallback';
import ProtectedRoute from './components/ProtectedRoute';
import { AuthProvider } from './contexts/AuthContext';
import VerifyEmailPendingPage from './pages/VerifyEmailPendingPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import VerifyBusinessSignUpPendingPage from './pages/VerifyBusinessSignUpPendingPage';
import VerifyBusinessSignUpPage from './pages/VerifyBusinessSignUpPage';
import SetPasswordPage from './pages/SetPasswordPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import { ThemeProvider } from './contexts/ThemeContext';
import ErrorBoundary from './components/ErrorBoundary';
import './services/FetchInterceptor';
import { globalErrorHandler } from './services/GlobalErrorHandler';

// Lazy load admin pages
const AdminDashboard = lazy(() => import('./admin/Dashboard'));
const AdminUsers = lazy(() => import('./admin/Users'));
const AdminOrganizations = lazy(() => import('./admin/Organizations'));
const AdminChallenges = lazy(() => import('./admin/Challenges'));
const AdminAnalytics = lazy(() => import('./admin/Analytics'));
const AdminLeaderboards = lazy(() => import('./admin/Leaderboards'));


const AdminLayout = lazy(() => import('./admin/AdminLayout'));

const App: React.FC = () => {
  useEffect(() => {
    // Initialize global error handler
    globalErrorHandler.initialize();
  }, []);

  return (
    <Router>
      <ThemeProvider>
        <AuthProvider>
          <ErrorBoundary>
            {/* <AuthDebug /> */}
            <Routes>
              {/* Public routes - no authentication required */}
              <Route path="/" element={<LandingPage />} />
              <Route path="/homepage" element={<LandingPage />} />
              <Route path="/company" element={<LandingPage />} />
              <Route path="/contact-sales" element={<ContactSalesPage />} />
              <Route path="/terms" element={<TermsOfUsePage />} />
              <Route path="/privacy" element={<PrivacyPolicyPage />} />
              <Route path="/about" element={<AboutUsPage />} />
              {/* All website login routes redirect to admin login */}
              <Route path="/login" element={
                <ProtectedRoute requireAuth={false}>
                  <AdminSubscriptionLoginPage />
                </ProtectedRoute>
              } />
              {/* Admin/Subscription Management Login */}
              <Route path="/admin/login" element={
                <ProtectedRoute requireAuth={false}>
                  <AdminSubscriptionLoginPage />
                </ProtectedRoute>
              } />
              <Route path="/subscription/login" element={
                <ProtectedRoute requireAuth={false}>
                  <AdminSubscriptionLoginPage />
                </ProtectedRoute>
              } />
              {/* App/Mobile User Login - separate route for app users */}
              <Route path="/app/login" element={
                <ProtectedRoute requireAuth={false} requireSubscription={true}>
                  <LoginScreen />
                </ProtectedRoute>
              } />
              <Route path="/callback" element={<AzureADCallback />} />
              <Route path="/verify-email-pending" element={<VerifyEmailPendingPage />} />
              <Route path="/verify-email" element={<VerifyEmailPage />} />
              <Route path="/verify-business-signup-pending" element={<VerifyBusinessSignUpPendingPage />} />
              <Route path="/verify-business-signup" element={<VerifyBusinessSignUpPage />} />
              <Route path="/set-password" element={<SetPasswordPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/accept-family-invitation" element={<AcceptFamilyInvitation />} />

              {/* Protected routes - authentication and subscription required */}
              <Route path="/home" element={
                <ProtectedRoute requireSubscription={true}>
                  <HomeScreen />
                </ProtectedRoute>
              } />
              <Route path="/dashboard" element={
                <ProtectedRoute requireSubscription={true}>
                  <HomeScreen />
                </ProtectedRoute>
              } />
              <Route path="/user-settings" element={
                <ProtectedRoute requireSubscription={true}>
                  <UserSettingsScreen />
                </ProtectedRoute>
              } />
              <Route path="/challenge" element={
                <ProtectedRoute requireSubscription={true}>
                  <ChallengeScreen />
                </ProtectedRoute>
              } />
              <Route path="/challenge-progress/:challengeId" element={
                <ProtectedRoute requireSubscription={true}>
                  <ChallengeProgressScreen />
                </ProtectedRoute>
              } />
              <Route path="/challenge/:challengeId/photos" element={
                <ProtectedRoute requireSubscription={true}>
                  <ChallengeProgressScreen />
                </ProtectedRoute>
              } />
              <Route path="/leaderboard" element={
                <ProtectedRoute requireSubscription={true}>
                  <LeaderboardScreen />
                </ProtectedRoute>
              } />
              <Route path="/settings" element={
                <ProtectedRoute requireSubscription={true}>
                  <SettingsScreen />
                </ProtectedRoute>
              } />
              <Route path="/join-challenges" element={
                <ProtectedRoute requireSubscription={true}>
                  <JoinChallengeScreen />
                </ProtectedRoute>
              } />

              {/* Subscription routes - authentication required */}
              <Route path="/onboarding" element={
                <ProtectedRoute>
                  <OnboardingPage />
                </ProtectedRoute>
              } />
              <Route path="/subscription" element={
                <ProtectedRoute>
                  <SubscriptionPage />
                </ProtectedRoute>
              } />
              <Route path="/subscription/upgrade" element={
                <ProtectedRoute>
                  <SubscriptionUpgradePage />
                </ProtectedRoute>
              } />
              <Route path="/subscription/success" element={
                <ProtectedRoute>
                  <SubscriptionSuccessPage />
                </ProtectedRoute>
              } />
              <Route path="/subscription/cancel" element={
                <ProtectedRoute>
                  <SubscriptionCancelPage />
                </ProtectedRoute>
              } />
              <Route path="/subscription/challenges" element={
                <ProtectedRoute>
                  <ChallengesPage />
                </ProtectedRoute>
              } />
              <Route path="/subscription/reporting" element={
                <ProtectedRoute>
                  <ReportingPage />
                </ProtectedRoute>
              } />
              <Route path="/subscription/organizations" element={
                <ProtectedRoute>
                  <OrganizationsPage />
                </ProtectedRoute>
              } />
              <Route path="/subscription/users" element={
                <ProtectedRoute>
                  <UsersPage />
                </ProtectedRoute>
              } />
              <Route path="/subscription/leaderboards" element={
                <ProtectedRoute>
                  <LeaderboardsPage />
                </ProtectedRoute>
              } />
              <Route path="/subscription/invoices" element={
                <ProtectedRoute>
                  <InvoicesPage />
                </ProtectedRoute>
              } />
              <Route path="/subscription/history" element={
                <ProtectedRoute>
                  <HistoryPage />
                </ProtectedRoute>
              } />
              <Route path="/subscription/settings" element={
                <ProtectedRoute>
                  <SettingsPage />
                </ProtectedRoute>
              } />

              {/* Admin routes - authentication required */}
              <Route path="/admin" element={
                <ProtectedRoute>
                  <Suspense fallback={<div>Loading...</div>}>
                    <AdminLayout />
                  </Suspense>
                </ProtectedRoute>
              }>
                <Route index element={<AdminDashboard />} />
                <Route path="analytics" element={<AdminAnalytics />} />
                <Route path="leaderboards" element={<AdminLeaderboards />} />
                <Route path="users" element={<AdminUsers />} />
                <Route path="organizations" element={<AdminOrganizations />} />
                <Route path="challenges" element={<AdminChallenges />} />
              </Route>

              {/* Catch all route - redirect authenticated users to dashboard */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </ErrorBoundary>
        </AuthProvider>
      </ThemeProvider>
    </Router>
  );
};

export default App; 